import { z } from 'zod';

import { parseISO8601DateTime } from '@/utils/dates';
import { zodTimeStamp } from '@/utils/zodUtils';

/**
 * Branding pushed to the operator's Stripe connected account and shown on the
 * invoices it issues. `logo_url` is a short-lived presigned S3 URL, present
 * only when a logo is stored (omitted on a transient presign failure).
 * `synced_at` is the last successful push to Stripe — null/absent means the
 * branding has never been synced.
 */
export const BankAccountBranding = z.object({
  logo_url: z.string().nullish(),
  brand_color: z.string().nullish(),
  accent_color: z.string().nullish(),
  public_name: z.string().nullish(),
  support_email: z.string().nullish(),
  support_phone: z.string().nullish(),
  support_url: z.string().nullish(),
  synced_at: zodTimeStamp.nullish(),
});

export const BankAccount = z.object({
  id: z.string(),
  name: z.string(),
  onboarded: z.boolean(),
  external_id: z.string(),
  customer_id: z.string(),
  branding: BankAccountBranding.nullish(),
  created_at: z.string().transform((val) => parseISO8601DateTime(val)),
  updated_at: z
    .string()
    .transform((val) => parseISO8601DateTime(val))
    .nullable(),
});

export type BankAccountBrandingType = z.infer<typeof BankAccountBranding>;
export type BankAccountType = z.infer<typeof BankAccount>;

/**
 * Partial update payload for `PUT /bank_accounts/:id` (multipart). Every field
 * is optional; omit a field to leave it unchanged. `logo` is sent to Stripe
 * only when present.
 */
export interface UpdateBankAccountBrandingInput {
  name?: string;
  public_name?: string;
  brand_color?: string;
  accent_color?: string;
  support_email?: string;
  support_phone?: string;
  support_url?: string;
  logo?: File;
}
export const BankAccountList = z.array(BankAccount);
export type BankAccountListType = z.infer<typeof BankAccountList>;
